import { useState } from "react";
import {
	Box,
	Grid,
	Paper,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import { GridColDef } from "@mui/x-data-grid";
import { Dispatch, SetStateAction } from "react";
import { useGridApiRef } from "@mui/x-data-grid";
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import ItemTable from "../../components/ItemTable";
import { Item } from "../../util/Item";
import { updateActiveTask, Util } from "../../util/Util";
import { MYSQL_LIMITS } from "../../util/constants";
import { useSale, SaleTask } from "./SaleContext";
import SaleDetail from "./SaleDetail";
import PaymentDetail from "./PaymentDetail";
import PosCatalogGrid from "./PosCatalogGrid";
import PosCartList from "./PosCartList";
import { useUI } from "../../providers/MoleUIContext";
import { useAppSocket } from "../../providers/AppSocketContext";
import {
	PosCartViewMode,
	readStoredPosCartViewMode,
	writeStoredPosCartViewMode,
} from "./posLayouts";

import { addMoney } from "../../util/quantity";
interface Props {
	renderChildren: boolean;
	setValidateDetail: (value: boolean) => void;
	bundle?: any;
	/** A saved sale is being amended (not a new sale or an invoiced quote). */
	amending?: boolean;
	columns: GridColDef<Item>[];
	allowPosAliasing: boolean;
	totalEnteredAmount: number;
	apiRef: ReturnType<typeof useGridApiRef<GridApiCommunity>>;
	setPendingTasks: Dispatch<SetStateAction<SaleTask<Item>[]>>;
	getMinSellPrice?: (row: Item) => number;
}

/**
 * Split layout: 50/50 browse + cart/payment.
 * Mirrors Visual upgrades (barcode catalog, cart table/list, no classic SaleItemDetail).
 */
const PosSplitLayout = ({
	renderChildren,
	setValidateDetail,
	bundle,
	amending = false,
	columns,
	allowPosAliasing,
	totalEnteredAmount,
	apiRef,
	setPendingTasks,
	getMinSellPrice,
}: Props) => {
	const { activeTask, items } = useSale();
	const { t } = useUI();
	const { appSettings } = useAppSocket();
	const [cartView, setCartView] = useState<PosCartViewMode>(() => readStoredPosCartViewMode());

	const cartLines = activeTask?.items ?? [];
	const cartCount = cartLines.length;
	const cartTotal = cartLines.reduce((sum, row) => addMoney(sum, (Number(row.total) || 0)), 0);
	const currency = appSettings?.BIZ_CURRENCY?.value || "USD";

	const handleCartViewChange = (_: unknown, next: PosCartViewMode | null) => {
		if (!next) return;
		setCartView(next);
		writeStoredPosCartViewMode(next);
	};

	return (
		<Grid container spacing={1} sx={{ height: "100%", minHeight: 0 }}>
			{/* Left: header + visual catalog */}
			<Grid
				size={{ xs: 12, md: 6 }}
				sx={{
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
					height: { md: "100%" },
					gap: 1,
				}}
			>
				<Paper elevation={24} sx={{ flexShrink: 0 }}>
					{renderChildren && (
						<SaleDetail setValidateCheck={setValidateDetail} amending={amending} />
					)}
				</Paper>
				<Paper
					elevation={24}
					sx={{
						flex: 1,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
						p: 1,
					}}
				>
					{renderChildren && (
						// compact tiles suit the narrower half; barcode + filters match Visual
						<PosCatalogGrid bundle={bundle} compact showBarcode />
					)}
				</Paper>
			</Grid>

			{/* Right: cart (table|list) + notes + payment */}
			<Grid
				size={{ xs: 12, md: 6 }}
				sx={{
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
					height: { md: "100%" },
					gap: 1,
				}}
			>
				{renderChildren && (
					<Paper
						elevation={24}
						sx={{
							flex: 1,
							minHeight: 180,
							display: "flex",
							flexDirection: "column",
							overflow: "hidden",
						}}
					>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "1fr auto 1fr",
								alignItems: "center",
								gap: 1,
								px: 1.25,
								py: 0.75,
								borderBottom: 1,
								borderColor: "divider",
								flexShrink: 0,
							}}
						>
							<Typography variant="subtitle2" fontWeight={700} sx={{ justifySelf: "start" }}>
								{t("common:pos.cart.title")}
							</Typography>
							<Typography
								variant="body2"
								fontWeight={700}
								sx={{
									justifySelf: "center",
									textAlign: "center",
									color: "text.primary",
									px: 1,
								}}
							>
								{t("common:ui.labels.count", {
									num: Util.formatNumber(cartCount),
									total: Util.formatCurrency(cartTotal, currency),
								})}
							</Typography>
							<Box sx={{ justifySelf: "end" }}>
								<ToggleButtonGroup
									exclusive
									size="small"
									value={cartView}
									onChange={handleCartViewChange}
									aria-label={t("common:pos.cart.viewToggleAria")}
								>
									<ToggleButton value="table" aria-label={t("common:pos.cart.table")}>
										<Tooltip title={t("common:pos.cart.table")}>
											<Box
												component="span"
												sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
											>
												<TableRowsOutlinedIcon fontSize="small" />
												<Box
													component="span"
													sx={{ display: { xs: "none", lg: "inline" } }}
												>
													{t("common:pos.cart.table")}
												</Box>
											</Box>
										</Tooltip>
									</ToggleButton>
									<ToggleButton value="list" aria-label={t("common:pos.cart.list")}>
										<Tooltip title={t("common:pos.cart.list")}>
											<Box
												component="span"
												sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
											>
												<ViewAgendaOutlinedIcon fontSize="small" />
												<Box
													component="span"
													sx={{ display: { xs: "none", lg: "inline" } }}
												>
													{t("common:pos.cart.list")}
												</Box>
											</Box>
										</Tooltip>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</Box>

						<Box
							sx={{
								flex: 1,
								minHeight: 0,
								overflow: "auto",
								p: cartView === "list" ? 1 : 0,
							}}
						>
							{cartView === "table" ? (
								<ItemTable<Item, SaleTask<Item>>
									activeTask={activeTask}
									setPendingTasks={setPendingTasks}
									columns={columns}
									itemConstructor={Item}
									bundle={bundle}
									items={items}
									lineNameMode="sale"
									allowPosAliasing={allowPosAliasing}
									apiRef={apiRef}
									hideFooter
									getMinSellPrice={getMinSellPrice}
								/>
							) : (
								<PosCartList bundle={bundle} amending={amending} setPendingTasks={setPendingTasks} />
							)}
						</Box>
					</Paper>
				)}

				<Paper elevation={24} sx={{ flexShrink: 0 }}>
					<TextField
						id="details"
						size="small"
						label={t("common:ui.labels.notes")}
						variant="filled"
						fullWidth
						type="text"
						value={activeTask?.description || ""}
						inputProps={{ maxLength: MYSQL_LIMITS.TEXT.maxLength }}
						onChange={(e) => updateActiveTask(setPendingTasks, { description: e.target.value })}
						disabled={!activeTask}
					/>
				</Paper>

				<Box
					sx={{
						flexShrink: 0,
						maxHeight: { xs: "none", md: "42%" },
						overflow: "auto",
						minHeight: 0,
					}}
				>
					{renderChildren && (
						<PaymentDetail totalEnteredAmount={totalEnteredAmount} amending={amending} />
					)}
				</Box>
			</Grid>
		</Grid>
	);
};

export default PosSplitLayout;
